import { graphqlErrorCode } from '../../utils/graphqlErrors';
import { omit } from 'lodash/fp';
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ORGANISATIONS } from '../../graphql/organisation';
import { PROJECTS } from '../../graphql/project';
import { useMaxFiles } from '../../hooks/modules/project/usePointCloudDrop';
import { usePreviewUpload, useUpload } from '../../hooks/useUpload';
import { ProjectModal } from '../../modules/project/ProjectModal';
import { T, useT } from '../../translation/src';
import { useProjectDeleteMutation, useProjectInitializeMutation, useProjectStartMutation, useProjectStartWithPythagorasFilesMutation } from '../../types/graphqlTypes';
import { SubmitModal } from '../Modal/SubmitModal';
import { ProgressBar } from '../loaders/ProgressBar';
import { UserContext } from '../../contexts/UserContext';

const DEFAULT_TAB_TITLE = 'Pointorama';

const useTranslations = () => {
  const insufficientCredits = useT('You have no projects left. Buy projects, or contact sales for a subscription with monthly projects included.');
  const subscriptionPaused = useT('Your subscription is paused. Uploads resume when it does.');
  const uploadFailed = useT('The upload could not be started. Please try again.');
  return { insufficientCredits, subscriptionPaused, uploadFailed };
};

interface FormValues {
  name: string;
  description: string;
  previewImage?: FileList;
  shouldMerge: boolean;
}

interface UploadProgressModalProps {
  onClose: () => void;
  open?: boolean;
  files?: File[];
  isPythagorasFile?: boolean;
}
const UploadProgressModal_: React.FC<UploadProgressModalProps> = ({ onClose, files, open: progressModalOpen, isPythagorasFile }) => {
  const { organisationId = '' } = useParams();
  const [initializeProject] = useProjectInitializeMutation();
  const [{ progress }, { uploadFiles, resetProgress, onCancelUpload }] = useUpload();
  const [formValues, setFormValues] = useState<FormValues | undefined>();
  const [deleteProject] = useProjectDeleteMutation();
  const [startProject] = useProjectStartMutation();
  const [startProjectWithPythagorasFiles] = useProjectStartWithPythagorasFilesMutation();
  const [setAndUploadPreview] = usePreviewUpload();
  const maxFiles = useMaxFiles();
  const windowFocussed = useRef(true);
  const uploadInterval = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const uploadProjectId = useRef<string | undefined>(undefined);
  const currentUser = useContext(UserContext);
  const translations = useTranslations();

  const onPreventBrowserClose = useCallback((e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = 'Are you sure you want to close the browser? Your upload will be cancelled.';
    return e.returnValue;
  }, []);

  const onReset = useCallback(() => {
    uploadProjectId.current = undefined;
    setFormValues(undefined);
    resetProgress();
  }, [resetProgress]);

  const onSuccessProjectCreate = useCallback(() => {
    window.removeEventListener('beforeunload', onPreventBrowserClose);
    onClose();
    onReset();
  }, [onPreventBrowserClose, onClose, onReset]);

  const runUpload = useCallback(
    async (values: FormValues) => {
      uploadProjectId.current = undefined;
      if (!files?.length || (!currentUser.isSuperAdmin && files.length > maxFiles)) return;
      const { data } = await initializeProject({ variables: { organisationId } });
      const projectId = data?.projectInitialize.id;
      uploadProjectId.current = projectId;
      if (!projectId) return;
      const uploadedFiles = await uploadFiles({
        files,
        projectId,
        isPythagorasFile,
      });
      // This means that the upload is cancelled
      if (!uploadProjectId.current) return;

      const createInfo = {
        fileNames: (uploadedFiles || []).map(({ name }) => name),
        cloudNames: (uploadedFiles || []).map(({ cloudName }) => cloudName),
        projectId,
      };

      const { previewImage, ...formData } = values;

      const uploadPreview = async (completedProjectId: string) => {
        if (previewImage) {
          const file = Array.from(previewImage)[0];
          if (file) await setAndUploadPreview(file, completedProjectId);
        }
      };

      if (!isPythagorasFile) {
        await startProject({
          variables: { ...formData, organisationId, ...createInfo },
          refetchQueries: [{ query: PROJECTS, variables: { organisationId } }, { query: ORGANISATIONS }],
          awaitRefetchQueries: true,
          onCompleted: async (data) => {
            // From here on the project is real — never discard it as a stub.
            uploadProjectId.current = undefined;
            await uploadPreview(data.projectStart.id);
          },
        });
      } else {
        await startProjectWithPythagorasFiles({
          variables: {
            ...formData,
            organisationId,
            cloudName: createInfo.cloudNames[0],
            pointCount: uploadedFiles?.[0]?.pointCount,
            ...omit('fileNames', createInfo),
          },
          refetchQueries: [{ query: PROJECTS, variables: { organisationId } }],
          awaitRefetchQueries: true,
          onCompleted: async (data) => {
            uploadProjectId.current = undefined;
            await uploadPreview(data.projectStartWithPythagorasFiles.id);
          },
        });
      }

      onSuccessProjectCreate();
    },
    [isPythagorasFile, files, initializeProject, uploadFiles, organisationId, maxFiles, currentUser.isSuperAdmin, startProject, startProjectWithPythagorasFiles, setAndUploadPreview, onSuccessProjectCreate],
  );

  // Without this the modal sits on "uploading" forever when the server refuses
  // the upload — most often because another upload took the last credit.
  const startUpload = useCallback(
    async (values: FormValues) => {
      try {
        await runUpload(values);
      } catch (error) {
        const code = graphqlErrorCode(error);
        toast.error(code === 'INSUFFICIENT_CREDITS' ? translations.insufficientCredits : code === 'SUBSCRIPTION_PAUSED' ? translations.subscriptionPaused : translations.uploadFailed);
        // An initialised-but-never-started stub would keep reserving a credit
        // for a day; drop it so the user can retry right away.
        const stubProjectId = uploadProjectId.current;
        if (stubProjectId) {
          try {
            await deleteProject({ variables: { projectId: stubProjectId }, update: (cache) => cache.evict({ id: `Project:${stubProjectId}` }) });
          } catch (e) {
            console.error(e);
          }
        }
        onSuccessProjectCreate();
      }
    },
    [runUpload, deleteProject, onSuccessProjectCreate, translations.insufficientCredits, translations.subscriptionPaused, translations.uploadFailed],
  );

  const onFormSubmit = useCallback(
    (values: FormValues) => {
      setFormValues(values);
      startUpload(values);
    },
    [startUpload],
  );

  const onCloseUploadProgressModal = useCallback(async () => {
    const projectId = uploadProjectId.current;
    uploadProjectId.current = undefined;
    onCancelUpload();
    if (uploadInterval.current) clearInterval(uploadInterval.current);
    if (projectId) {
      try {
        deleteProject({
          variables: { projectId },
          update: (cache) => cache.evict({ id: `Project:${projectId}` }),
        });
      } catch (e) {
        console.error(e);
      }
    }
    onReset();
    onClose();
  }, [deleteProject, onClose, onReset, onCancelUpload]);

  const onCloseProjectModal = useCallback(() => {
    onReset();
    onClose();
  }, [onReset, onClose]);

  const uploading = !!formValues;
  const projectModalOpen = progressModalOpen && !formValues;

  const onWindowFocus = useCallback(() => {
    document.title = DEFAULT_TAB_TITLE;
    if (uploadInterval.current) clearInterval(uploadInterval.current);
    windowFocussed.current = true;
  }, []);
  const onWindowBlur = useCallback(() => {
    windowFocussed.current = false;
  }, []);

  const open = progressModalOpen;

  useEffect(() => {
    if (open) window.addEventListener('beforeunload', onPreventBrowserClose);
    else window.removeEventListener('beforeunload', onPreventBrowserClose);
    return () => {
      window.removeEventListener('beforeunload', onPreventBrowserClose);
    };
  }, [onPreventBrowserClose, open]);
  useEffect(() => {
    return () => {
      window.removeEventListener('beforeunload', onPreventBrowserClose);
    };
  }, [onPreventBrowserClose]);
  useEffect(() => {
    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [onWindowBlur, onWindowFocus]);
  useEffect(() => {
    if (uploading && !windowFocussed.current) {
      uploadInterval.current = setInterval(() => {
        document.title = 'Upload done!';
        setTimeout(() => {
          document.title = DEFAULT_TAB_TITLE;
        }, 1000);
      }, 2000);
    }
    return () => {
      if (uploadInterval.current) clearInterval(uploadInterval.current);
    };
  }, [uploading]);

  return (
    <>
      <ProjectModal key="projectmodal" onClose={onCloseProjectModal} open={projectModalOpen} isPythagorasFile={isPythagorasFile} onFormSubmit={onFormSubmit} fileCount={files?.length} />
      <SubmitModal open={progressModalOpen && uploading} title={<T _str="Uploading pointclouds" />} onCancel={onCloseUploadProgressModal}>
        <div className="flex flex-col items-center justify-center">
          <div className="text-sm text-gray-500">
            <T _str="We are uploading pointclouds. Please don't close this window while uploading. Clicking on cancel will stop the upload" />
          </div>
          <ProgressBar progress={progress} />
        </div>
      </SubmitModal>
    </>
  );
};
export const UploadProgressModal = memo(UploadProgressModal_);
